import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeagueSchedule } from '@/hooks/useLeagueSchedule';
import { addDays, format } from 'date-fns';
import { es } from 'date-fns/locale';

interface TennisEncounterInfo {
  encounterNumber: number;
  dateRangeLabel: string; // "23 feb - 1 mar"
  weekStartDate: Date;
}

/**
 * For Tennis leagues, resolves the current or next upcoming encounter
 * with its 7-day date range label, accounting for rest weeks.
 */
export function useTennisEncounterForPreview(eventId: string, sport: string, isLeague: boolean, startDate: string) {
  const isTennis = isLeague && sport?.toLowerCase() === 'tenis';
  
  const { gameDates, loading: scheduleLoading } = useLeagueSchedule(isTennis ? eventId : '');
  const [restWeeks, setRestWeeks] = useState<number[]>([]);
  const [eventTotalWeeks, setEventTotalWeeks] = useState<number>(0);
  const [eventStartDate, setEventStartDate] = useState<string>('');
  const [rulesLoading, setRulesLoading] = useState(false);

  // Fetch restWeeks, total_weeks, and start_date from event
  useEffect(() => {
    if (!isTennis) return;
    
    setRulesLoading(true);
    supabase
      .from('events')
      .select('rules, start_date, total_weeks, start_time, end_time')
      .eq('id', eventId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          // Save total_weeks and start_date for fallback
          if (data.total_weeks) setEventTotalWeeks(data.total_weeks);
          if (data.start_date) setEventStartDate(data.start_date);
          
          if (data.rules) {
            try {
              const parsed = typeof data.rules === 'string' ? JSON.parse(data.rules) : data.rules;
              const rw = parsed.restWeeks || parsed.ladderConfig?.restWeeks || [];
              setRestWeeks(Array.isArray(rw) ? rw : []);
            } catch {
              setRestWeeks([]);
            }
          }
        }
        setRulesLoading(false);
      });
  }, [eventId, isTennis]);

  const encounterInfo = useMemo<TennisEncounterInfo | null>(() => {
    if (!isTennis) return null;

    // Determine base date and total chrono weeks
    let baseDateLocal: Date;
    let totalChronoWeeks: number;

    if (gameDates.length > 0) {
      // Primary: use gameDates from tournament_schedules
      const firstDateStr = gameDates[0]?.date;
      if (!firstDateStr) return null;

      const dateMatch = firstDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        baseDateLocal = new Date(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]));
      } else {
        const baseDate = new Date(firstDateStr);
        baseDate.setHours(0, 0, 0, 0);
        baseDateLocal = baseDate;
      }
      totalChronoWeeks = gameDates.length + restWeeks.length;
    } else if (eventTotalWeeks > 0 && eventStartDate) {
      // Fallback: use event's start_date and total_weeks
      const dateMatch = eventStartDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        baseDateLocal = new Date(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]));
      } else {
        baseDateLocal = new Date(eventStartDate);
        baseDateLocal.setHours(0, 0, 0, 0);
      }
      totalChronoWeeks = eventTotalWeeks;
    } else {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build chronological weeks, mapping encounter numbers (skipping rest weeks)
    let encounterIdx = 0;
    let bestMatch: TennisEncounterInfo | null = null;
    let firstFuture: TennisEncounterInfo | null = null;

    for (let i = 0; i < totalChronoWeeks; i++) {
      const chronoWeekNum = i + 1;
      const isRest = restWeeks.includes(chronoWeekNum);
      
      if (isRest) continue;
      
      encounterIdx++;
      const weekStart = addDays(baseDateLocal, i * 7);
      const weekEnd = addDays(weekStart, 6);
      
      const label = `${format(weekStart, 'd MMM', { locale: es })} - ${format(weekEnd, 'd MMM', { locale: es })}`;

      // Check if today falls within this week range
      if (today >= weekStart && today <= weekEnd) {
        bestMatch = { encounterNumber: encounterIdx, dateRangeLabel: label, weekStartDate: weekStart };
        break;
      }

      // Track the first future week
      if (!firstFuture && weekStart > today) {
        firstFuture = { encounterNumber: encounterIdx, dateRangeLabel: label, weekStartDate: weekStart };
      }
    }

    return bestMatch || firstFuture || null;
  }, [isTennis, gameDates, restWeeks, eventTotalWeeks, eventStartDate]);

  return {
    encounterInfo,
    isTennis,
    loading: scheduleLoading || rulesLoading,
  };
}
